import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, RefreshCw, Search, Building2, CheckCircle2, PauseCircle } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { LEAD_TYPE_LABEL } from "../components/leads/leadConfig";

const AdminPartners = () => {
  useSeo({ title: "Vastgoedpartners · Admin", description: "Partners beheren" });
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ total: 0, active: 0 });
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ active_only: false, lead_type: "", region: "" });

  const load = () => {
    setRows(null); setErr("");
    const qs = new URLSearchParams();
    if (q.trim()) qs.set("q", q.trim());
    if (filters.active_only) qs.set("active_only", "true");
    if (filters.lead_type) qs.set("lead_type", filters.lead_type);
    if (filters.region.trim()) qs.set("region", filters.region.trim());
    api(`/admin/partners?${qs.toString()}`).then((d) => {
      setRows(d.rows || []);
      setMeta({ total: d.total, active: d.active });
    }).catch((e) => setErr(e.message));
  };
  useEffect(load, [filters]); // eslint-disable-line

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/admin" className="inline-flex items-center gap-1 hover:text-navy"><ArrowLeft size={14} /> Admin</Link>
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-navy" data-testid="admin-partners-title">Vastgoedpartners</h1>
          <p className="text-sm text-slate-500">{meta.total} partners · {meta.active} actief</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw size={14} className="mr-1" /> Ververs</Button>
          <Button asChild className="rounded-xl bg-navy text-amber-300 hover:bg-navy/90" data-testid="partner-new">
            <Link to="/admin/partners/new"><Plus size={14} className="mr-1.5" /> Nieuwe partner</Link>
          </Button>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-6 grid gap-3 rounded-3xl border border-border bg-white p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative lg:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="rounded-xl pl-9" placeholder="Zoek op naam, contact of e-mail" value={q} onChange={(e) => setQ(e.target.value)} data-testid="partner-q" />
        </div>
        <select className="rounded-xl border border-border bg-white px-3 py-2 text-sm" value={filters.lead_type} onChange={(e) => set("lead_type", e.target.value)} data-testid="partner-f-type">
          <option value="">Alle leadtypes</option>
          {Object.entries(LEAD_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Input className="rounded-xl" placeholder="Regio/stad" value={filters.region} onChange={(e) => set("region", e.target.value)} data-testid="partner-f-region" />
        <label className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 text-sm">
          <input type="checkbox" checked={filters.active_only} onChange={(e) => set("active_only", e.target.checked)} data-testid="partner-f-active" />
          Alleen actief
        </label>
      </form>

      {err && <div className="mt-4 rounded-xl bg-danger/10 p-4 text-sm text-danger">{err}</div>}

      <div className="mt-6">
        {!rows ? (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-500"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-border bg-white p-10 text-center shadow-sm">
            <Building2 className="mx-auto text-slate-300" size={40} />
            <p className="mt-4 text-slate-500">Nog geen partners. Voeg er een toe om te starten.</p>
            <Button asChild className="mt-4 rounded-xl bg-navy text-amber-300 hover:bg-navy/90">
              <Link to="/admin/partners/new"><Plus size={14} className="mr-1.5" /> Nieuwe partner</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="partner-grid">
            {rows.map((p) => (
              <Link
                to={`/admin/partners/${p.id}`}
                key={p.id}
                className="group rounded-3xl border border-border bg-white p-5 shadow-sm transition hover:border-brandblue hover:shadow-md"
                data-testid={`partner-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-lg font-extrabold text-navy group-hover:text-brandblue">{p.name}</h3>
                  {p.active ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-semibold text-success"><CheckCircle2 size={12} /> Actief</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500"><PauseCircle size={12} /> Inactief</span>
                  )}
                </div>
                {p.contact_name && <p className="mt-0.5 text-xs text-slate-500">{p.contact_name}</p>}
                {p.email && <p className="mt-2 truncate text-xs text-slate-500">{p.email}</p>}
                <div className="mt-3 flex flex-wrap gap-1">
                  {(p.lead_types || []).slice(0, 4).map((t) => (
                    <span key={t} className="rounded-full bg-brandblue-light px-2 py-0.5 text-[11px] font-semibold text-brandblue">{LEAD_TYPE_LABEL[t] || t}</span>
                  ))}
                  {(p.lead_types || []).length > 4 && <span className="text-[11px] text-slate-400">+{p.lead_types.length - 4}</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                  {(p.regions || []).length > 0 && <span>Regio: {(p.regions || []).slice(0, 3).join(", ")}{p.regions.length > 3 ? "…" : ""}</span>}
                  {p.price_per_lead != null && <span>€{p.price_per_lead}/lead</span>}
                  <span className="capitalize">{p.exclusivity}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartners;
